import { useViewConfig } from 'src/hooks/useViewConfig';
import { crosstableUrl, rootUrl, cloudflareFileServerUrl } from 'src/constants';
import { jsPDF } from 'jspdf';
import { convertToPNG } from 'src/helpers/svgToPng';
import { useSelector } from 'react-redux';
import { useBuildType, useGetRequestAppData } from 'src/hooks';
import { useEffect, useRef, useState } from 'react';
import { useQRCode } from 'src/hooks/useQRCode';
import { getShowFlatshow } from 'src/store/requests/app/selector';
import { IFilterAllData, ListOfKeysFilterAllData } from 'src/typings/filter';
import {
  addDays, addMonths, format, Locale, Month,
} from 'date-fns';
import { IPlacementPdfOrderingEnum, IPlacementPdfOrderings } from 'src/typings/pdf';
import download from 'downloadjs';
import { getView2dData } from 'src/store/view-2d/selectors';
import { useMergePdf } from 'src/hooks/useMergePdf';
import { insertHtmlForPdf } from 'src/helpers/insertHtmlForPdf';
import { getPDFDocumentsOrderRequest } from 'src/api/paymentPlan';
import { IComplex } from 'src/typings/complexes';
import {
  enUS, uk, ru, ka, de, tr, hy, pl, fr, ar,
} from 'date-fns/locale';
import { processEmoji } from 'src/helpers/emojisParser';
import { commonFont, languageFontsMap } from 'src/helpers/pdfFonts';

export const useCreatePdf = () => {
  const { isPaymentPlansSeparated, getViewConfig, checkIsPaymentPlansSeparated } = useViewConfig();
  const isShowFlatshow = useSelector(getShowFlatshow);
  const { isCrossTable } = useBuildType();
  const [loading, setloading] = useState(false);
  const { getQrCode, getLinkMarketplace } = useQRCode();
  const { appRequestData } = useGetRequestAppData();
  const view2dData = useSelector(getView2dData);
  const [openPDFPaymentPlansModal, setOpenPDFPaymentPlansModal] = useState<boolean>(false);
  const [selectedPDFPaymentPlan, setSelectedPDFPaymentPlan] = useState<any>(null);
  const {
    mergedPdfFiles,
  } = useMergePdf();
  const { isMarketplace } = useBuildType();

  // PDF generation is a multi-second await chain; the host component (sidebar
  // actions, popover) can unmount mid-flight. The document may still finish
  // and download — only the state updates need the guard.
  const mountedRef = useRef(true);
  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  useEffect(() => {
    insertHtmlForPdf();
  }, []);

  function convertAllSVGToPNG(callback: any) {
    const promises: any[] = [];
    const tempPdf = document.querySelector('#tempPDF');
    if (tempPdf) {
      // @ts-ignore
      tempPdf.querySelectorAll('img').forEach((img) => promises.push(convertToPNG(img, img.closest('#pdf').style.backgroundColor)));
    }
    if (callback) Promise.all(promises).then(callback);
  }

  function getUrlExtension(url: string) {
    // @ts-ignore
    return url.split(/[#?]/)[0].split('.').pop().trim().toLowerCase();
  }

  function rotate(srcBase64: string, degrees: number, color: any, callback: ((arg0: string) => void) | undefined) {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const image = new Image();

      image.onload = function () {
        canvas.width = degrees % 180 === 0 ? image.width : image.height;
        canvas.height = degrees % 180 === 0 ? image.height : image.width;

        if (getUrlExtension(image.src) === 'svg') {
          canvas.width *= 4;
          canvas.height *= 4;
          image.width *= 4;
          image.height *= 4;
        }
        if (canvas.width > canvas.height) canvas.height = canvas.width;
        if (canvas.height > canvas.width) canvas.width = canvas.height;
        if (ctx) {
          ctx.fillStyle = color;
          // draw background / rect on entire canvas
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.translate(canvas.width / 2, canvas.height / 2);
          if (degrees !== 0) {
            ctx.rotate(degrees * (Math.PI / 180));
          }
          ctx.drawImage(image, canvas.width / -2 + 36, canvas.height / -2 + 36, image.width - 72, image.height - 72);
        }

        // @ts-ignore
        callback(canvas.toDataURL('image/jpeg', 1.0));
        // @ts-ignore
        resolve();
      };
      image.crossOrigin = 'Anonymous';
      image.src = srcBase64;
    });
  }

  function calculateDate(this: string, descriptionColumnValue: string = '') {
    const forceNominative = (baseLocale: Locale): Locale => {
      return {
        ...baseLocale,
        localize: {
          ...baseLocale.localize,
          month: (n: number, opts: any = {}) => {
            if (baseLocale.localize?.month) {
              return baseLocale.localize.month(n as Month, { width: opts.width || 'wide', context: 'standalone' });
            }
            return n.toString();
          },
        },
      };
    };

    const Locales: Record<string, Locale> = {
      en: forceNominative(enUS),
      uk: forceNominative(uk),
      pl: forceNominative(pl),
      ru: forceNominative(ru),
      ge: forceNominative(ka),
      de: forceNominative(de),
      tr: forceNominative(tr),
      fr: forceNominative(fr),
      hy: forceNominative(hy),
      ar: forceNominative(ar),
    };

    const lang = this;

    const currentDate = new Date();
    const dateRegex = /{currentDate(?:\s*\+\s*(\d+)_month)?(?:\s*\+\s*(\d+)_day)?(?:\s*:\s*([A-Z]{2,3})_format)?}/;

    const matches = dateRegex.exec(descriptionColumnValue);

    if (matches) {
      const monthsToAdd = matches[1] ? parseInt(matches[1], 10) : 0;
      const daysToAdd = matches[2] ? parseInt(matches[2], 10) : 0;

      let resultDate = addMonths(currentDate, monthsToAdd);
      resultDate = addDays(resultDate, daysToAdd);

      const matchesFormat = matches[3];
      let dateFnsFormat: string;

      switch (matchesFormat) {
        case 'MY':
          dateFnsFormat = 'MMMM yyyy';
          break;
        case 'MDY':
          dateFnsFormat = 'MMMM d yyyy';
          break;
        default:
          dateFnsFormat = 'MMMM yyyy';
          break;
      }

      const locale = Locales[lang] || enUS;
      return format(resultDate, dateFnsFormat, { locale });
    }

    return descriptionColumnValue;
  }

  const combine = (imgSource: string, overlay: any, color: any) => {
    return new Promise(((resolve) => {
      const img = new Image();
      img.onload = function () { resolve(img); };
      img.crossOrigin = 'Anonymous';
      img.src = imgSource;
    })).then((image: any) => {
      const clientWidth = image.naturalWidth;
      const clientHeight = image.naturalHeight;
      const canvas = document.createElement('canvas');

      canvas.width = clientWidth;
      canvas.height = clientHeight;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = color; // <- background color
        // draw background / rect on entire canvas
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(image, 0, 0, clientWidth, clientHeight);
        if (overlay.pointsArray) {
          overlay.pointsArray.forEach((point: string) => {
            const pointsArray = point.split(' ');
            ctx.moveTo(Number(pointsArray[0]), Number(pointsArray[1]));
            for (let item = 2; item < pointsArray.length - 1; item += 2) {
              ctx.lineTo(Number(pointsArray[item]), Number(pointsArray[item + 1]));
            }
          });
        } else {
          const pointsArray = overlay.points.split(' ');
          ctx.moveTo(pointsArray[0], pointsArray[1]);
          for (let item = 2; item < pointsArray.length - 1; item += 2) {
            ctx.lineTo(pointsArray[item], pointsArray[item + 1]);
          }
        }
        ctx.fillStyle = `${overlay.fill}B3`;
        ctx.fill();
        ctx.strokeStyle = overlay.stroke;
        ctx.stroke();
        ctx.closePath();
        // ctx.drawImage(svg, 0, 0, clientWidth, clientHeight);
      }

      // return the resulting image in base64 form
      return canvas.toDataURL('image/jpeg');
    });
  };

  const generatePDF = async (
    doc: jsPDF,
    placementOrderings: IPlacementPdfOrderings[],
    complexName: string,
    lang: string,
    placement?: Partial<IFilterAllData>,
  ) => {
    const foundLanguageWithLongWords = languageFontsMap.find((font) => font.lang === lang);
    const regularFont = foundLanguageWithLongWords ? foundLanguageWithLongWords.regularFontUrl : commonFont.regularFontUrl;
    const boldFont = foundLanguageWithLongWords ? foundLanguageWithLongWords.boldFontUrl : commonFont.boldFontUrl;
    await document.fonts.ready;
    doc.addFont(`${cloudflareFileServerUrl}/font/${regularFont}`, 'PdfFont', 'normal');
    doc.addFont(`${cloudflareFileServerUrl}/font/${boldFont}`, 'PdfFont-Bold', 'bold');
    doc.setFont('PdfFont'); // set font
    doc.setFont('PdfFont-Bold'); // set font

    const count = document.querySelectorAll('#pdf').length;

    const tempPdfEl = document.querySelector('#tempPDF');
    if (tempPdfEl) {
      if (foundLanguageWithLongWords) {
        tempPdfEl.classList.add('languageWithLongWords');
      }
      const house: any = placement ? placement[ListOfKeysFilterAllData.placementHouse] : '';
      const isOneSection = view2dData && placement && house && Object.keys(view2dData[house]).length === 1;
      const currentDate = new Date();
      const formattedDate = format(currentDate, 'MMM dd, yyyy');
      const appTitle = `${isCrossTable ? appRequestData?.crosstable_title : appRequestData?.module_title}`;
      const fileTitle = placement ? `${!isMarketplace ? appTitle : complexName}_${
        placement[ListOfKeysFilterAllData.placementHouse]}${
        !isOneSection ? `_${placement[ListOfKeysFilterAllData.placementSection]}` : ''}_${
        placement[ListOfKeysFilterAllData.placementFloor]}_${
        placement[ListOfKeysFilterAllData.placementPlanningType]}_${
        placement[ListOfKeysFilterAllData.placementNumeration]}|${
        placement[ListOfKeysFilterAllData.placementTotalArea]
      }|${formattedDate}` : 'Apartment_Passport';
      if (placementOrderings.length) {
        doc.html(tempPdfEl as HTMLElement, {
          callback: async () => {
            doc.deletePage(count + 1);
            const result = placementOrderings.map((item) => {
              if (item.type === IPlacementPdfOrderingEnum.EXTERNAL) return `${item.fileUri?.fileUrl}`;
              if (item.type === IPlacementPdfOrderingEnum.INTERNAL) return doc.output('arraybuffer');
              return '';
            });
            const mergedFile = await mergedPdfFiles(result);
            download(mergedFile, `${fileTitle}.pdf`, 'application/pdf');
            if (!mountedRef.current) {
              const tempPdfCleanup = document.getElementById('tempPDF');
              if (tempPdfCleanup) {
                tempPdfCleanup.innerHTML = '';
              }
              return;
            }
            // const formSubmitGTMEvent = complex?.frontConfig?.website.gtm.events.find((event) => event.type === IGtmEventsTypes.pdfDownload);
            // if (!isCrossTable && !isDevMode && isProdEnv() && window.dataLayer && formSubmitGTMEvent && typeof formSubmitGTMEvent.eventJson === 'object' && !Array.isArray(formSubmitGTMEvent.eventJson)) {
            //   window.dataLayer.push(formSubmitGTMEvent.eventJson);
            // }
            setloading(false);
            const tempPdf = document.getElementById('tempPDF');
            if (tempPdf) {
              tempPdf.innerHTML = '';
            }
          },
          margin: [0, 0, 0, 0],
          x: 0,
          y: 0,
        });
      }
    }
  };

  const handlerIcon = async (
    placementData: Partial<IFilterAllData>,
    complex: Partial<IComplex>,
    complexName: string,
    lang: string,
    // isPaymentPlanBtn:boolean = false,
    selectedPaymentPlans?: any | null,
    openPaymentPlansModal?: (state: boolean) => void,
    onCustomPDF?: (error: Error) => void,
  ) => {
    const viewConfig = await getViewConfig(placementData?.id || '');
    if (!mountedRef.current) return;
    if (checkIsPaymentPlansSeparated(viewConfig) && !selectedPaymentPlans) {
      if (openPaymentPlansModal) openPaymentPlansModal(true);
      return;
    }
    const href = `${rootUrl}/client/module/get-placement-pdf-data?lang=${lang}&id=${placementData.id}&frontType=${isCrossTable ? 'crosstable' : 'module'}`;
    let compass: any;
    setloading(true);
    fetch(href)
      .then((res) => res.json())
      .then(async (data) => {
        const { data: docs } = await getPDFDocumentsOrderRequest(String(complex.id));
        const placementPDFDocuments = docs['hydra:member'][0].placementPdfOrderings;
        const qrCodeLink = getLinkMarketplace(complex);
        const qrCode = await getQrCode(qrCodeLink, { color: { light: '#0000' } });
        const pdfViewTypeRes = await fetch(
          `${rootUrl}/client/module/get-placement-pdf-config-view-type-field?id=${complex.id}&frontType=${isCrossTable ? 'crosstable' : 'module'}`,
        );
        const pdfViewTypeData = await pdfViewTypeRes.json();
        const onePageData: any = {};
        const onePageTrigger = pdfViewTypeData.view_type === 'one-page';
        const { backgroundColor, clientLogoURL } = data[0];
        const currentDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        // eslint-disable-next-line new-cap
        const doc = new jsPDF('p', 'pt', 'a4');
        const rectX = 523;
        const rectY = 37;
        const rectW = 35;
        const rectH = 36;

        let pageNumber = 0;
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < data.length; i++) {
          const item = data[i];
          if (!item.firstImageURL) {
            // eslint-disable-next-line no-continue
            continue;
          } else {
            pageNumber += 1;
          }
          item.notes = item.notes ? [...item.notes] : [];
          item.qrCode = qrCode;
          item.isShowFlatshow = !isMarketplace ? isShowFlatshow : true;
          doc.rect(rectX, rectY, rectW, rectH, 'f');
          doc.link(rectX, rectY, rectW, rectH, { url: qrCodeLink });
          doc.addPage();
          item.pageNumber = pageNumber;
          item.currentDate = currentDate;
          if (item.floorWindroseData) {
            // eslint-disable-next-line no-await-in-loop
            const response = await fetch(`${rootUrl}/entity-options/get-spinner-config?entity=floor&id=${item.floorWindroseData.id}`);
            // eslint-disable-next-line no-await-in-loop
            const res = await response.json();
            item.thirdImageURL = res._windRose.src;
            item.degree = res._windRose.degree;

            if (item.thirdImageURL) {
              // eslint-disable-next-line no-await-in-loop,no-loop-func
              await rotate(item.thirdImageURL, item.degree, item.backgroundColor, (resultBase64: any) => {
                compass = resultBase64;
              });
            }
          }
          if (item.firstImageOverlaySVGData) {
            // eslint-disable-next-line no-await-in-loop
            const result = await combine(item.firstImageURL, item.firstImageOverlaySVGData, item.backgroundColor);
            item.firstImageURL = result;
          }
          if (item.secondImageOverlaySVGData) {
            // eslint-disable-next-line no-await-in-loop
            const result = await combine(item.secondImageURL, item.secondImageOverlaySVGData, item.backgroundColor);
            item.secondImageURL = result;
          }
          item.thirdImageURL = compass;
          if (onePageTrigger) {
            if (i == 0) {
              onePageData.salesDepartmentFieldName = item.salesDepartmentFieldName;
              onePageData.complexAddressFieldName = item.complexAddressFieldName;
              onePageData.backgroundColor = item.backgroundColor;
              onePageData.complexAddress = item.complexAddress;
              onePageData.apartmentLayout = item.firstImageURL;
              onePageData.placementData = item.placementData;
              onePageData.clientLogoURL = item.clientLogoURL;
              onePageData.complexName = item.complexName;
              onePageData.phones = item.phones || [];
              onePageData.notes = item.notes || [];
              onePageData.compass = compass;
              onePageData.isShowFlatshow = item.isShowFlatshow;
            } else if ((i == 1 && data.length === 2) || (i == 1 && data.length === 3) || (i == 2 && data.length === 4)) {
              onePageData.floorPlan = item.firstImageURL;
              onePageData.sectionDiagram = item.secondImageURL;
            } else if ((i == 2 && data.length === 2) || (i == 2 && data.length === 3) || (i == 3 && data.length === 4)) onePageData.renderFacade = item.firstImageURL;
          } else {
            // @ts-ignore
            // eslint-disable-next-line no-undef
            document.getElementById('tempPDF').innerHTML += tmpl('pdf-template', item);
          }
        }
        if (onePageTrigger) {
          // @ts-ignore
          // eslint-disable-next-line no-undef
          document.getElementById('tempPDF').innerHTML += tmpl('pdf-template-short', onePageData);
        }
        if (!selectedPaymentPlans || (selectedPaymentPlans && selectedPaymentPlans.type !== 'common')) {
          let paymentResult;
          if (selectedPaymentPlans?.type || !selectedPaymentPlans) {
            const pdfPaymentRes = await fetch(
              `${crosstableUrl}/mapped_placement_pdf_payment_plans?language=${lang}&placementId=${placementData.id}&frontType=${isCrossTable ? 'crosstable' : 'module'}`,
            );
            paymentResult = await pdfPaymentRes.json();
          } else {
            paymentResult = [selectedPaymentPlans];
          }
          if (paymentResult?.length) {
            let currentPageNumber = !isPaymentPlansSeparated ? data.length : 0;
            // eslint-disable-next-line no-plusplus
            for (let i = 0; i < paymentResult.length; i++) {
              const paymentData = paymentResult[i];
              currentPageNumber += 1;
              paymentData.name = paymentData.name?.toUpperCase() || '';
              paymentData.uniqueUnitName = paymentData.uniqueUnitName?.toUpperCase() || '';
              paymentData.notes = paymentData.notes ? [...paymentData.notes] : [];
              paymentData.pageNumber = currentPageNumber;
              paymentData.clientLogoURL = clientLogoURL;
              paymentData.backgroundColor = backgroundColor;
              paymentData.calculateDate = calculateDate.bind(lang);
              paymentData.currentDate = currentDate;
              paymentData.isShowFlatshow = isShowFlatshow;
              // @ts-ignore
              // eslint-disable-next-line no-undef
              document.getElementById('tempPDF').innerHTML += tmpl('payment-template', paymentData);
              const tempPDFContainer = document.getElementById('tempPDF');
              if (tempPDFContainer) {
                processEmoji.parse(tempPDFContainer);
                const isNeedNewBlock = (block: Element): boolean => {
                  let result = false;
                  // eslint-disable-next-line no-plusplus
                  for (let c = 0; c < block.children.length; c++) {
                    // @ts-ignore
                    if (block.children[c].offsetTop > footerTopOffset && !block.children[c].classList.contains('paymentPageFooter')) {
                      result = true;
                      break;
                    }
                  }
                  return result;
                };

                // eslint-disable-next-line no-loop-func
                const buildNewBlock = (block: Element): HTMLElement => {
                  currentPageNumber += 1;

                  // @ts-ignore
                  const newBlock = block.cloneNode(true) as HTMLElement;

                  // @ts-ignore
                  const newfooter = newBlock.querySelector('.paymentPageFooter');
                  if (newfooter) {
                    const pageNumberElement = newfooter.querySelector('.pageNumber');
                    if (pageNumberElement) {
                      pageNumberElement.textContent = `${currentPageNumber}`;
                    }
                  }

                  // @ts-ignore
                  // const newHeader = newBlock.querySelector('.paymentPageHeader').cloneNode(true);
                  // @ts-ignore
                  while (newBlock.children.length > 3) {
                    // @ts-ignore
                    newBlock.removeChild(newBlock.children[2]);
                  }

                  // eslint-disable-next-line no-plusplus
                  for (let c = block.children.length - 1; c >= 0; c--) {
                    // @ts-ignore
                    if (block.children[c].offsetTop > footerTopOffset && !block.children[c].classList.contains('paymentPageFooter')) {
                      // @ts-ignore
                      newBlock.insertBefore(block.children[c], newBlock.children[2]);
                    }
                  }
                  // @ts-ignore
                  // newBlock.insertBefore(newHeader, newBlock.children[1]);
                  tempPDFContainer.appendChild(newBlock);
                  return newBlock;
                };

                let lastBlock = tempPDFContainer.lastElementChild;
                if (!lastBlock) {
                  return;
                }

                const footer = lastBlock.querySelector('.paymentPageFooter');
                if (!footer) {
                  return;
                }
                // @ts-ignore
                const footerTopOffset = footer.offsetTop - 40;

                // Проверяем, есть ли элементы, которые не помещаются в текущий блок
                let needNewBlock = isNeedNewBlock(lastBlock);
                while (needNewBlock) {
                  lastBlock = tempPDFContainer.lastElementChild;
                  if (lastBlock) {
                    const newBlock = buildNewBlock(lastBlock);
                    needNewBlock = isNeedNewBlock(newBlock);
                  }
                }
              }
            }
          }
        }
        setTimeout(() => {
          convertAllSVGToPNG(() => generatePDF(doc, placementPDFDocuments, complexName, lang, placementData));
        }, 1000);
      })
      .catch((err: Error) => {
        // Surface the failure — a swallowed error here looks like "the download
        // just silently does nothing" (e.g. tmpl.min.js missing on the host).
        // eslint-disable-next-line no-console
        console.error('[pdf] generation failed', err);
        if (onCustomPDF) {
          onCustomPDF(err);
        }
        if (mountedRef.current) {
          setloading(false);
        }
      });
  };

  return {
    handlerIcon,
    isPaymentPlansSeparated,
    loading,
    openPDFPaymentPlansModal,
    selectedPDFPaymentPlan,
    setOpenPDFPaymentPlansModal,
    setSelectedPDFPaymentPlan,
  };
};
